import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BellSimpleIcon } from '@phosphor-icons/react';
import useSWR from 'swr';
import { toast } from '@/components';
import { CACHE_KEYS } from '@/constants/cache-keys';
import { EngineService } from '@/services/engine';
import { FullAlertRule } from '@/types';
import { SourceType } from '@/types/source';
import { getApiErrorMessage } from '@/utils/error-messages';
import { AlertRuleActionBar } from './components/AlertRuleActionBar';
import { AlertRuleInfoPanel } from './components/AlertRuleInfoPanel';
import { AlertChart } from './components/AlertChart';
import { SegmentedControl } from '@/components';
import { I18nNamespace } from '@/lib/i18n-namespaces';
import { getAlertRuleDisplayName } from '@/utils/alert-rules';
import {
    activationExceedsAlertUnitLimit,
    spendingSourceCount,
} from '@/features/AlertRuleCreation/alert-unit-limit';
import { revalidateAlertUnitCapacity } from '@/features/SettingsView/hooks/useConfiguration';

interface AlertRuleViewProps {
    alertRule: FullAlertRule;
    onUpdate?: (updated: FullAlertRule) => void;
    onDelete?: () => void;
    isDeleting?: boolean;
}

export function AlertRuleView({
    alertRule,
    onUpdate,
    onDelete,
    isDeleting,
}: AlertRuleViewProps) {
    const { t } = useTranslation([
        I18nNamespace.EVENTS,
        I18nNamespace.ALERT_RULES,
    ]);
    const [isUpdating, setIsUpdating] = useState(false);
    const [optimisticActive, setOptimisticActive] = useState<boolean | null>(
        null,
    );

    const displayActive = optimisticActive ?? alertRule.active;
    const { data: alertUnitCapacity } = useSWR(
        [CACHE_KEYS.ALERT_UNIT_CAPACITY, alertRule.id],
        () => EngineService.getAlertUnitCapacity(alertRule.id),
    );
    const { data: liveCameraIds } = useSWR(
        [CACHE_KEYS.SOURCES_PREFIX, 'ids', SourceType.CAMERA],
        () => EngineService.getSourceIds({ type: SourceType.CAMERA }),
    );
    const activationBlocked = useMemo(() => {
        if (alertRule.active) return false;
        return activationExceedsAlertUnitLimit(
            alertUnitCapacity?.usedAlertUnits ?? 0,
            spendingSourceCount(
                alertRule.sources.map((source) => source.id),
                liveCameraIds ? new Set(liveCameraIds) : null,
            ),
            alertUnitCapacity === undefined
                ? undefined
                : alertUnitCapacity.maxAlertUnits,
        );
    }, [alertRule.active, alertRule.sources, alertUnitCapacity, liveCameraIds]);
    const activationBlockedMessage = useMemo(() => {
        if (!activationBlocked) return undefined;
        if (!alertUnitCapacity) {
            return t('errors.organization.alert_unit_count_unavailable', {
                ns: I18nNamespace.SETTINGS,
            });
        }
        if (alertUnitCapacity.maxAlertUnits == null) return undefined;
        const needed = spendingSourceCount(
            alertRule.sources.map((source) => source.id),
            liveCameraIds ? new Set(liveCameraIds) : null,
        );
        const left = Math.max(
            0,
            alertUnitCapacity.maxAlertUnits - alertUnitCapacity.usedAlertUnits,
        );
        if (left === 0) {
            return t('form.sourceSelection.alertLimit.activateBlockedNone', {
                ns: I18nNamespace.ALERT_RULES,
                needed,
            });
        }
        if (left === 1) {
            return t('form.sourceSelection.alertLimit.activateBlockedOne', {
                ns: I18nNamespace.ALERT_RULES,
                needed,
            });
        }
        return t('form.sourceSelection.alertLimit.activateBlockedSome', {
            ns: I18nNamespace.ALERT_RULES,
            needed,
            left,
        });
    }, [
        activationBlocked,
        alertRule.sources,
        alertUnitCapacity,
        liveCameraIds,
        t,
    ]);

    const handleStatusChange = async (status: 'active' | 'inactive') => {
        if (!alertRule || isUpdating) return;

        const newActive = status === 'active';
        if (alertRule.active === newActive) return;
        if (newActive && activationBlocked) return;

        setOptimisticActive(newActive);
        setIsUpdating(true);

        try {
            const updated = await EngineService.updateAlertRule(alertRule.id, {
                active: newActive,
            });
            onUpdate?.(updated);
            await revalidateAlertUnitCapacity();
            toast.success(
                t(
                    newActive
                        ? 'toast.activateSuccess'
                        : 'toast.deactivateSuccess',
                    { ns: I18nNamespace.ALERT_RULES, count: 1 },
                ),
            );
        } catch (error) {
            console.error('Failed to update alert rule status:', error);
            toast.error(
                getApiErrorMessage(
                    error,
                    t('toast.updateStatusError', {
                        ns: I18nNamespace.ALERT_RULES,
                    }),
                ),
            );
        } finally {
            setOptimisticActive(null);
            setIsUpdating(false);
        }
    };

    return (
        <div className="h-full flex flex-col relative overflow-hidden pt-14">
            <div className="absolute top-6 left-1/2 -translate-x-1/2 z-50">
                <AlertRuleActionBar
                    alertRule={alertRule}
                    onDelete={onDelete}
                    isDeleting={isDeleting}
                />
            </div>
            <div className="flex-1 flex overflow-y-auto scrollbar-hide px-4">
                <div className="flex-1 flex flex-col gap-6 pt-8 pb-24 min-w-0 max-w-content mx-auto">
                    <div className="flex flex-wrap items-center gap-4">
                        <div className="w-12 h-12 flex items-center justify-center rounded bg-grey-500">
                            <BellSimpleIcon className="w-6 h-6 text-grey-300" />
                        </div>
                        <div className="flex-1 min-w-0">
                            <div className="text-sm text-grey-200 mb-1">
                                {t('view.alertRule')}
                            </div>
                            <h1 className="text-lg text-white-100">
                                {getAlertRuleDisplayName(alertRule)}
                            </h1>
                        </div>
                        <SegmentedControl
                            value={displayActive ? 'active' : 'inactive'}
                            onValueChange={handleStatusChange}
                            isLoading={isUpdating}
                            options={[
                                {
                                    value: 'active',
                                    label: t('infoPanel.active'),
                                    disabled:
                                        !displayActive && activationBlocked,
                                    disabledReason: activationBlockedMessage,
                                },
                                {
                                    value: 'inactive',
                                    label: t('infoPanel.inactive'),
                                },
                            ]}
                        />
                    </div>

                    <div className="flex flex-col border-b border-grey-500 @wide:flex-row">
                        <AlertRuleInfoPanel alertRule={alertRule} />
                    </div>

                    <div className="flex-1 min-h-0">
                        <AlertChart alertRuleId={alertRule.id} />
                    </div>
                </div>
            </div>
        </div>
    );
}
